import React, { useState } from "react";
import { Button, Col, Form, Row, Spinner, InputGroup, Card, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faLock, faCheck, faTimes, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { postApi } from "../redux/api";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

export default function ProfileUpdatePassword({ form, setForm }) {
  const auth = useSelector((auth) => auth.auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    comfirm_password: false
  });
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }

    // Check password strength for new password
    if (name === "new_password") {
      checkPasswordStrength(value);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    if (password.length >= 8) {
      score += 1;
    } else {
      feedback.push("At least 8 characters");
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Include lowercase letters");
    }

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Include uppercase letters");
    }

    if (/\d/.test(password)) {
      score += 1;
    } else {
      feedback.push("Include numbers");
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Include special characters");
    }

    setPasswordStrength({ score, feedback });
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 1) return "danger";
    if (passwordStrength.score <= 2) return "warning";
    if (passwordStrength.score <= 3) return "info";
    return "success";
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 1) return "Weak";
    if (passwordStrength.score <= 2) return "Fair";
    if (passwordStrength.score <= 3) return "Good";
    return "Strong";
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!form.old_password) {
      newErrors.old_password = "Current password is required";
    }
    
    if (!form.new_password) {
      newErrors.new_password = "New password is required";
    } else if (form.new_password.length < 6) {
      newErrors.new_password = "Password must be at least 6 characters";
    } else if (form.old_password === form.new_password) {
      newErrors.new_password = "New password must be different from current password";
    }
    
    if (!form.comfirm_password) {
      newErrors.comfirm_password = "Please confirm your password";
    } else if (form.new_password !== form.comfirm_password) {
      newErrors.comfirm_password = "Passwords do not match";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validatePasswordForm()) {
      toast.error("Please fix the errors in the password form");
      return;
    }

    if (!auth?.username) {
      toast.error("User session invalid. Please log in again.");
      return;
    }

    setIsLoading(true);

    const data = {
      oldPassword: form.old_password,
      newPassword: form.new_password,
      username: auth.username,
    };

    postApi("treasury-app/change_password", data)
      .then((resp) => {
        if (resp.success) {
          toast.success("Password updated successfully! Please log in with your new password.");
          setForm(prev => ({
            ...prev,
            old_password: "",
            new_password: "",
            comfirm_password: "",
          }));
          setErrors({});
          setPasswordStrength({ score: 0, feedback: [] });
        } else {
          toast.error(resp.msg || "Failed to update password");
        }
      })
      .catch((err) => {
        console.error("Password change error:", err);
        toast.error("An error occurred while updating password");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Card className="mt-4 shadow-sm profile-password-card">
      <Card.Header className="bg-light">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faLock} className="me-2 text-primary" />
          <h5 className="mb-0">Change Password</h5>
        </div>
      </Card.Header>
      
      <Card.Body className="p-4">
        <Alert variant="info" className="mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          <strong>Security Tips:</strong> Use a strong password with at least 8 characters, including uppercase, lowercase, numbers, and special characters.
        </Alert>

        <div>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Current Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    type={showPasswords.old_password ? "text" : "password"}
                    value={form.old_password || ""}
                    name="old_password"
                    onChange={handleChange}
                    placeholder="Enter your current password"
                    isInvalid={!!errors.old_password}
                    className="border-end-0"
                  />
                  <InputGroup.Text 
                    className="bg-white border-start-0 cursor-pointer"
                    onClick={() => togglePasswordVisibility('old_password')}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon 
                      icon={showPasswords.old_password ? faEyeSlash : faEye} 
                      className="text-muted"
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.old_password}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  New Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    type={showPasswords.new_password ? "text" : "password"}
                    value={form.new_password || ""}
                    name="new_password"
                    onChange={handleChange}
                    placeholder="Enter your new password"
                    isInvalid={!!errors.new_password}
                    className="border-end-0"
                  />
                  <InputGroup.Text 
                    className="bg-white border-start-0 cursor-pointer"
                    onClick={() => togglePasswordVisibility('new_password')}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon 
                      icon={showPasswords.new_password ? faEyeSlash : faEye} 
                      className="text-muted"
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.new_password}
                  </Form.Control.Feedback>
                </InputGroup>
                
                {/* Password Strength Indicator */}
                {form.new_password && (
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-1">
                      <small className="text-muted me-2">Password Strength:</small>
                      <span className={`badge bg-${getPasswordStrengthColor()}`}>
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <div className="progress" style={{ height: '4px' }}>
                      <div 
                        className={`progress-bar bg-${getPasswordStrengthColor()}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      ></div>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <div className="mt-1">
                        {passwordStrength.feedback.map((tip, index) => (
                          <small key={index} className="text-muted d-block">
                            <FontAwesomeIcon icon={faTimes} className="text-danger me-1" />
                            {tip}
                          </small>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Confirm New Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    type={showPasswords.comfirm_password ? "text" : "password"}
                    value={form.comfirm_password || ""}
                    name="comfirm_password"
                    onChange={handleChange}
                    placeholder="Confirm your new password"
                    isInvalid={!!errors.comfirm_password}
                    className="border-end-0"
                  />
                  <InputGroup.Text 
                    className="bg-white border-start-0 cursor-pointer"
                    onClick={() => togglePasswordVisibility('comfirm_password')}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon 
                      icon={showPasswords.comfirm_password ? faEyeSlash : faEye} 
                      className="text-muted"
                    />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.comfirm_password}
                  </Form.Control.Feedback>
                </InputGroup>
                
                {/* Password Match Indicator */}
                {form.comfirm_password && form.new_password && (
                  <div className="mt-1">
                    {form.new_password === form.comfirm_password ? (
                      <small className="text-success">
                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                        Passwords match
                      </small>
                    ) : (
                      <small className="text-danger">
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        Passwords do not match
                      </small>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          
          <div className="d-flex justify-content-center">
            <Button 
              type="button" 
              variant="primary" 
              disabled={isLoading || !form.old_password || !form.new_password || !form.comfirm_password}
              className="px-4 py-2"
              onClick={handleUpdate}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating Password...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faLock} className="me-2" />
                  Update Password
                </>
              )}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
